import {EventsMgrProxy, IEventsMgr} from "lib/commons/events";
import {DOM, ENodeType} from "lib/commons/xml/dom";
import {IJmlObj, IJmlSet, IJmlSubSet, JML} from "lib/commons/xml/jml";
import {IXAddr, IXAddrRange, IXAddrSeq, XA} from "lib/commons/xml/xAddr";
import {IMsgFilter, MSGMETA_noCleanup} from "lib/edit/ot/houseOt";
import {ErrorMsg, IPublicDoor, IRoadNetProvider, Msg, PublicDoor, ResetStatesMsg, RoadLocal, RoadNet} from "lib/edit/ot/urban";
import {IXmlResetInitDatas, XmlBatch, XmlDeleteMsg, XmlInsertMsg, XmlMoveMsg, XmlSplitMsg, XmlStrMsg} from "lib/edit/ot/xmlHouse";
import {CommentHouseHolder, CommentsState, IXmlTypedHouseEvents, OXmlTypedHouseConfig, XmlTypedHouse} from "lib/edit/ot/xmlTypedHouse";
import {CARD, ECard, EPastePos, OSkExecOptions, Schema} from "lib/edit/schema/schema";
import {ISkAnnot, ISkStructDef} from "lib/edit/schema/schemaAnnots";
import {ISkRuleObj, SkRuleAttr, SkRuleNode} from "lib/edit/schema/schemaPatterns";
import {ISchemaSearch, ISkSearchAnnot} from "lib/edit/schema/schemaSearch";
import {ISkDiffAnnot, IXmlDiffSession} from "lib/edit/schema/diff";
import {IMoanState} from "lib/edit/ot/moan";
import IS_comment = DOM.IS_comment;

/**
 * API publique pour gérer et accéder aux espaces de stockage
 * d'un document Xml validé selon un schéma (IDocHolder).
 */
//class DocumentFactory {

export function createDocHolderLocal(schema: Schema, doc?: Document, buildOptions?: OSkExecOptions): DocHolder {
	//if (!annotsControllerConfig) annotsControllerConfig = {autoListenUpdatesOptions: SchemaDom.EXEC_OPTIONS_DEFAULT};
	return new DocHolder(new XmlTypedHouse({id: "@", initialDoc: doc, schema: schema, buildOptions: buildOptions, historyMinEntries: 300}));
}

/** Crée une 2è house synchronisée avec une autre IDocHolder. Pour test, aucun intérêt en prod, cf IDocHolder.cloneDocHolder() */
export function createDocHolderSynchedWith(other: IDocHolder): Promise<IDocHolder> {
	//if (!annotsControllerConfig) annotsControllerConfig = {autoListenUpdatesOptions: SchemaDom.EXEC_OPTIONS_DEFAULT};
	if (other instanceof DocHolder) {
		const config = {} as OXmlTypedHouseConfig;
		config.schema = other.house.schemaDom.schema;
		const newDoc = new DocHolder(new XmlTypedHouse(config));
		RoadLocal.connectMasterSlave(other.house, {}, newDoc.house, {});
		return newDoc.house.initFromMaster().then(() => newDoc);
	} else {
		throw Error("todo...");
	}
}

export function createDocHolderWs(ws: IRoadNetProvider, schema: Schema, buildOptions?: OSkExecOptions): Promise<IDocHolder> {
	const newDoc = new DocHolder(new XmlTypedHouse({
		schema: schema,
		buildOptions: buildOptions
	}));
	let resolve: (value?: any) => void;
	let reject: (reason?: any) => void;
	let state: 'waiting' | 'opened' | 'failed' = 'waiting';
	new RoadNet(ws, newDoc.house, {
		roadToMaster: true,
		// autoInitFromMaster: true,
		onOpen: function () {
			newDoc.house.initFromMaster().then(() => {
				state = 'opened';
				if (resolve) resolve(newDoc);
			});
		},
		onClose: function () {
			state = 'failed';
			if (reject) reject("WebSocket closed.");
		}
	});
	return new Promise((res: (value?: any) => void, rej: (reason?: any) => void) => {
		resolve = res;
		reject = rej;
		switch (state) {
		case 'opened':
			res(newDoc);
			break;
		case 'failed':
			rej("Init webSocket failed.");
			break;
		}
	});
}

//}

/** Paramètres et résultat pour récupérer tout ou partie du document en JML. */
export class FetchJml {
	result: IJmlSet | IJmlSubSet;

	/**
	 *
	 * @param xa 1er Noeud à retourner. Retourne le document si non spécifié.
	 * @param length Nombre de noeuds frères du 1er à retourner (1 par défaut).
	 * @param threshold Seuil maximum de contenus retourner.
	 */
	constructor(public xa?: IXAddr, public length?: number, public threshold?: number) {}
}

/** Paramètres et résultat pour récupérer tout ou partie des annotations relatives au schéma. */
export class FetchAnnots {
	result: Array<ISkAnnot>;

	/**
	 * @param xa Addresse du 1er élément pour lequel retourner les annotations. Seuls les Elements
	 *      sont porteurs des annotations. Les annotations relatives aux attributs ou aux noeuds Text
	 *      sont porés par leur élément parent.
	 * @param length Nombre de noeuds frères du 1er à parcourir.
	 * @param deep true par défaut. Si false ne retourne que les annotations associées à l'élément pointé par xa.
	 */
	constructor(public xa?: IXAddr, public length?: number, public deep?: boolean) {}
}

/** Paramètres et résultat pour récupérer un fragment du document sous différents formats possibles (xml, text, markdown...). */
export class FetchExport {
	result: Jsonisable;

	/**
	 *
	 * @param range Si null, tout le document.
	 * @param format Un des formats proposés par la XmlHouse et XmlTypedHouse.
	 * @param options Options propres au format.
	 */
	constructor(public range: IXAddrRange, public format: string, public options: Jsonisable) {}
}

export type IDocFetch = FetchJml | FetchAnnots | FetchExport;


/**
 * API publique pour gérer et accéder à un document.
 * Toutes les méthodes sont asynchrones pour autoriser une instanciation
 * de la house centralisée dans un sharedWorker ou une autre window (via postMessage).
 */
export interface IDocHolder {

	isDocHolderSync(): this is IDocHolderSync

	isAvailable: boolean

	/**
	 * Indique la racine de l'éditeur (si différent du document complet).
	 * Utilisé par exemple pour les sessions de diff pour filtrer les écarts concernant
	 * cet éditeur.
	 */
	setSubRoot(xa: IXAddr | null): void

	/**
	 * Retourne la racine de l'éditeur.
	 * Note : a pu évoluer depuis l'appel à setSubRoot().
	 */
	getSubRoot(): IXAddr

	/** Clone ce DocHolder pour créer un nouvel accès à la house (pour créer un 2ème éditeur connecté à la même house). */
	cloneDocHolder(): IDocHolder

	/**
	 * Récupération asynchrone d'un ensemble de contenus (jml, annots...).
	 */
	fetchContent(commands: IDocFetch[]): Promise<IDocFetch[]>

	/** Ce Msg a-t-il été produit via ce docHolder.*/
	isMsgFromUs(msg: Msg): boolean

	/** Listener des msg publiés par la house hébergeant le document. */
	addMsgListener(listener: (msg: Msg) => void): this

	removeMsgListener(listener: (msg: Msg) => void): this

	/**  Retourne le pilote d'un commentaire. */
	getCmtHouse(xAddr: IXAddr): CommentHouseHolder | null

	close(): void
}


/**
 * Document si il peut être accédé en synchrone.
 */
export interface IDocHolderSync extends IDocHolder {

	house: XmlTypedHouse

	door: IPublicDoor

	/** Wrapper de gestionnaire de listeners de la house pour éviter les memLeaks */
	houseLstn: IEventsMgr<IXmlTypedHouseEvents>

	/** Empêche le cleanup des balises inconnues / interdites. */
	noCleanup: boolean

	/** Document de la house. ATTENTION, ne jamais le modifier en direct ! */
	getDocument(): Document

	/**
	 * Copie du document avec forçage des déclarations des ns sur la racine.
	 * Peut ensuite être indenté via DOM.indentDom(docHolder.getDocumentForSave())
	 */
	getDocumentForSave(): Document

	/**
	 * Retourne le document entier ou un fragment.
	 */
	getContent(xa?: IXAddr, length?: number): IJmlSet

	/**
	 * Retourne le document entier ou un fragment avec un seuil maximum de contenus retourner.
	 */
	getContentThreshold(threshold: number, xa?: IXAddr, length?: number): IJmlSubSet

	/**
	 * Retourne les annotations de validation en cours pour tout le document ou un fragment.
	 *
	 * @param xa Addresse du 1er élément pour lequel retourner les annotations. Seuls les Elements
	 *      sont porteurs des annotations. Les annotations relatives aux attributs ou aux noeuds Text
	 *      sont porés par leur élément parent.
	 * @param length Nombre de noeuds frères du 1er à parcourir (1 par défaut).
	 * @param deep true par défaut. Si false ne retourne que les annotations associées à l'élément pointé par xa.
	 */
	getAnnots(xa?: IXAddr, length?: number, deep?: boolean): ISkAnnot[]

	/**
	 * Récupère un fragment du document sous différents formats possibles (xml, text, HTML, markdown...)
	 *
	 * @param range Si null, tout le document.
	 * @param format Un des formats proposés par la XmlHouse et XmlTypedHouse.
	 * @param formatOptions Options propres au format.
	 */
	exportRange(range: IXAddrRange, format: string, formatOptions?: Jsonisable): Jsonisable

	/**
	 * Récupère un ensemble de noeuds selon des filtres spécifiques sous différents formats possibles (xml, text, HTML, markdown...)
	 *
	 * @param xaNodes liste d'adresse des noeuds à exporter
	 * @param filters filtre pour chaque adresse de noeud à exporter
	 * @param format Un des formats proposés par la XmlHouse et XmlTypedHouse.
	 * @param formatOptions Options propres au format.
	 */
	exportNodes(xaNodes: IXAddr[], filters: Jsonisable[], format: string, formatOptions?: Jsonisable): Jsonisable

	/**
	 * Copie le range et ajoute dans le batch les msg de suppression de ce range.
	 * ATTENTION : garanti la présence d'un point d'insertion après le noeud texte de range.start (ie
	 * split le texte si le range est interne à un noeud texte).
	 * Ne pas utiliser pour les fonctions de cut/suppression de la sélection de textes (ie sans être suivi d'une insertion de noeuds au point d'insertion)
	 * car ne gère pas la fusion des 2 noeuds textes consécutifs qui pourrait résulter de la suppression du range.
	 *
	 * @return Retourne le paramètre to.
	 */
	extractRange(range: IXAddrRange, batch: IDocBatch, to: IJmlSet): IJmlSet

	/**
	 * Retourne la struct portant les informations du schéma et du méta-schéma
	 * pour le noeud ou l'attribut pointé par xa.
	 */
	getStruct(xa: IXAddr, virtualPath?: Array<IJmlObj | '#' | '@'>): ISkRuleObj

	/**
	 * Retourne un aspect du schéma pour un noeud donné.
	 */
	getAspect<A>(xa: IXAddr, virtualPath: null | Array<IJmlObj | '@'>, keyAspect: string, options?: any): A;

	/** Retourne la cardinalité de cet élément dans son contexte parent. Permet notamment de savoir si un noeud est supprimable, répétable... */
	getRealCard(xa: IXAddr): ECard | undefined

	/** Combine la card du schéma et le contexte du noeud (cas où card="+") pour évlauer si un noeud peut-être supprimé. */
	isRemovable(xa: IXAddr): boolean | undefined

	/**
	 * Retourne 1, 2, 3 ou 4 listes de structures insérables.
	 * @param parent Noeud parent à partir duquel rechercher les structs
	 * @param offset1 offset du 1er point d'insertion, correspondant à la 1ère liste ou -1 pour n'importe quel point d'insertion fils.
	 * 						(null si seule la liste des attributs est souhaitée).
	 * @param offset2 offset du 2ème point d'insertion, correspondant à la 2ème liste (null si la liste des attributs est souhaitée).
	 * @param attrs true pour retourner une liste des attributs insérables sur le parent.
	 * @param replaceables si non null, retourne une liste de plus es structs susceptibles de remplacer le noeud ou l'attr dont l'offset ou le nom est spécifié.
	 */
	getInsertableStructs(parent: IXAddr, offset1: number | -1 | null, offset2?: number | null, attrs?: boolean, replaceables?: number | string): (ISkStructDef | undefined)[][]

	/**
	 * Retourne 1 ou 2 listes des fils / attributs insérables, y compris dans un contexte virtuel.
	 * @param what: 'children' ou 'atts' retourne une liste, 'both' retourne 2 listes
	 * @param parent Contexte parent de la recherche des noeuds fils insérables.
	 * @param virtualPath tableau définissant des derniers noeuds virtuels (de type élement) du IXAddr parent.
	 * @param attrs Ajoute aussi les attributs.
	 */
	getInsertableStructsAll(what: 'children' | 'atts' | 'both', parent: IXAddr, virtualPath?: Array<IJmlObj>): (ISkStructDef | undefined)[][]

	/**
	 * Retourne les structures en alternatives à une autre, y compris dans un contexte virtuel.
	 * @param parent Contexte parent de la recherche d'alternatives.
	 * @param offset Offset d'insertion prévu des alternatives.
	 * @param nodeType Type du noeud/attribut dont on cherche les alternatives.
	 * @param nodeName Nom du noeud/attribut dont on cherche les alternatives.
	 * @param virtualPath tableau définissant des derniers noeuds virtuels (de type élement) du IXAddr parent.
	 */
	getAlternateStructs(parent: IXAddr, offset: number, nodeType: ENodeType, nodeName: string, virtualPath?: Array<IJmlObj>): ISkStructDef[]

	/**
	 * Recherche l'offset d'insertion pour un noeud fils en fonction du schéma.
	 * Si le document contient des noeuds commentaires au point d'insertion, l'offset retourné est après le(s) commentaires(s).
	 * Pour un type attribut, retourne 0 si l'attribut est insérable, ie non présent et autorisé dans le schéma (-1 sinon, -2 si attr inconnu).
	 * @return
	 *    -1 si insertion impossible : struct déjà présente et (card=?|1 || ifExistAndCardN='reject'))
	 *    -2 si struct inconnue.
	 */
	getInsertableOffset(parent: IXAddr, nodeType: ENodeType, ifExistAndCardN: 'reject' | 'start' | 'end', nodeName?: string): number

	/**
	 * Recherche la position d'insertion possible de ce contenu autour de insertSeq.
	 * @param insertSeq représente une sélection de noeuds existants autour desquels envisager l'insertion.
	 *    La notion de séquence permet de représenter tout ce qui serait supprimé pour être remplacé par content
	 *    (autorisant ainsi des possiblités de remplacements avec des cardinalités ? ou 1).
	 *    Si on veut vérifier une insertion possible après le dernier fils, insertSeq.start doit pointer
	 *    ce dernier fils (avec insertSeq.len = 0 ou 1) et évaluer si EPastePos.after est autorisé.
	 * @return Combinaison binaire de EPastePos.
	 */
	getImportPos(insertSeq: IXAddrSeq, content: Node | IJmlObj): EPastePos | EPastePos

	/**
	 * Evalue si le schéma impose eltName1 avant eltName2.
	 * Retourne undefined si le schéma ne peut donner d'indication.
	 */
	checkSchemaOrder(xa: IXAddr, eltName1: string, eltName2: string): boolean | undefined

	/** Crée un batch. */
	newBatch(selBefore?: IXAddrRange): IDocBatch & XmlBatch

	/**
	 * Tente un undo, retourne Promise<false> si aucun undo n'a été possible.
	 */
	willUndo(filter?: IMsgFilter): Promise<boolean>

	/**
	 * Tente un redo, retourne Promise<false> si aucun redo n'a été possible.
	 * Note : en édition concurrente, il est impossible de savoir à l'avance si une action de undo
	 * et même de redo est disponible car elle a à tout moment pu être killed par une action concurrente.
	 */
	willRedo(filter?: IMsgFilter): Promise<boolean>

	/** Change la requête courante : 0 ou une seule requête courante par docHolder. */
	setSkSearch(search: ISchemaSearch | null): ISkSearchAnnot[]

	/** Retourne la requête en cours. */
	getSkSearch(): ISchemaSearch | null

	/** Change la session de diff courante : 0 ou une seule session de diff par docHolder. */
	setDiffSession(diffSession: IXmlDiffSession | null): void

	/** Retourne la session courante. */
	getDiffSession(): IXmlDiffSession | null

	/** */
	switchHouse(newHouse: XmlTypedHouse): void;
}

/** Batch de modification du document. */
export interface IDocBatch {

	readonly docHolder: IDocHolderSync

	readonly msgs: Msg[]

	readonly selBefore: IXAddrRange

	readonly selAfter: IXAddrRange

	/** Metas associé au batch (user, date, tableSel...) */
	getMeta(key: string): any

	setMeta(key: string, val: any): this

	getState(): 'empty' | 'building' | 'executed'


	/**
	 * Les prochains messages seront ajustés avec les msg déjà présents dans le batch.
	 */
	needAdjustForNextAdds(): this

	/** Ajoute un message. */
	add(msg: Msg): this

	/** Ajoute une liste de messages. */
	addAll(msgs: Msg[]): this

	setSelBefore(start: IXAddr | null, end?: IXAddr): this

	setSelAfterRange(val: IXAddrRange | null): this

	setSelAfterSeq(start: IXAddr, len: number): this

	setSelAfter(start: IXAddr | null, end?: IXAddr): this

	setSelAfterSkipAdjust(start: IXAddr | null, end?: IXAddr): this

	extendSelAfterRange(val: IXAddrRange): this

	extendSelAfterSeq(start: IXAddr, len: number): this

	extendSelAfter(start: IXAddr, end?: IXAddr): this

	/**
	 * Insert un contenu dans un interstice donné du DOM (ne split pas un noeud texte).
	 * @param xa point d'insertion
	 */
	insertJml(xa: IXAddr, jml: IJmlSet): this

	/**
	 * Insert un texte dans un noeud texte ou attribut.
	 * @param xa point d'insertion
	 */
	insertText(xa: IXAddr, text: string): this

	/**
	 * Coupe un noeud texte pour insérer un fragment.
	 * @param xa point d'insertion
	 */
	splitText(xa: IXAddr, lenToRemove: number, textToMove: string, jmlToInsert: IJmlSet): this

	/** Suppression d'une séquence. */
	deleteSequence(xa: IXAddr, length: number): this

	/**
	 * Suppression d'une sélection.
	 * Exploite le metaSchema pour ne pas supprimer les metas des containers de fin
	 * de sélection non entièrement supprimés.
	 */
	deleteRange(range: IXAddrRange): this

	/** Déplacement d'une séquence. */
	moveSequence(xa: IXAddr, jmlOrText: IJmlSet | string, xaTarget: IXAddr): this

	/** Remplacement d'une séquence. */
	spliceSequence(xa: IXAddr, delLen: number, jmlOrText: IJmlSet | string): this

	/** Remplace le contenu d'un noeud text ou comment. */
	setText(xa: IXAddr, value: string): this

	/**
	 * Ecrase ou supprime un attribut.
	 * A utiliser pour créer et supprimer un attribut et, lorsque l'attribut existe,
	 * pour modifier une valeur de façon atomique (énum, date, nombre...) ou l'insertion et la
	 * suppression concurrentes de caractères dans l'attribut seraient non pertinentes.
	 *
	 * @param xa Le dernier item de xa doit être le nom de l'attribut
	 * @param value Valeur de l'attribut, ou null pour le supprimer.
	 */
	setAttr(xa: IXAddr, value: string | null): this

	/** Déplacement d'un attribut. */
	renameOrMoveAttr(xaAtt: IXAddr, xaTarget: IXAddr): this

	/** Renomme un élément. */
	renameElt(xa: IXAddr, newName: string): this

	/** Execute le batch. */
	doBatch(): this
}

export class DocHolder implements IDocHolderSync {

	house: XmlTypedHouse;
	door: IPublicDoor;

	noCleanup: boolean;

	protected _subRoot: Node;

	protected _houseLstn: EventsMgrProxy<IXmlTypedHouseEvents>;

	protected _search: ISchemaSearch | null;

	protected _diffSession: IXmlDiffSession | null;

	get houseLstn(): IEventsMgr<IXmlTypedHouseEvents> {
		if (!this._houseLstn) this._houseLstn = new EventsMgrProxy(this.house.listeners);
		return this._houseLstn;
	}

	constructor(house: XmlTypedHouse) {
		this.house = house;
		this.door = house.openPublicDoor();
	}

	isDocHolderSync() {return true}

	get isAvailable(): boolean {return this.house != null}

	setSubRoot(xa: IXAddr | null): void {
		this._subRoot = xa ? XA.findDomLast(xa, this.house.root) : null;
	}

	getSubRoot(): IXAddr {return this._subRoot ? XA.fromNode(this._subRoot) : null}

	cloneDocHolder(): DocHolder {
		return new DocHolder(this.house);
	}

	close() {
		if (this._houseLstn) this._houseLstn.removeAllListeners();
		if (this.house) {
			if (this._search) {
				this.house.updateSkSearch(null, this._search);
				this._search = null;
			}
			if (this._diffSession) {
				this.house.doDiff(null, this._diffSession, [], []);
				this._diffSession = null;
			}
			this.house.closeDoor(this.door);
			this.door = null;
			this.house = null;
		}
	}

	getDocument(): Document {return this.house.document}

	getDocumentForSave(): Document {return this.house.getDocumentForSave()}

	getContent(xa?: IXAddr, length: number = 1): IJmlSet {
		if (!xa) {
			return JML.dom2jml(this.house.root);
		} else {
			let node = XA.findDomLast(xa, this.house.root);
			const jml = JML.dom2jml(node);
			for (let i = 1; i < length; i++) {
				node = node.nextSibling;
				JML.dom2jml(node, jml);
			}
			return jml;
		}
	}

	getContentThreshold(threshold: number, xa?: IXAddr, length: number = 1): IJmlSubSet {
		if (!xa) {
			return JML.dom2jmlThreshold(this.house.root, threshold);
		} else {
			let node = XA.findDomLast(xa, this.house.root);
			const jml: IJmlSubSet = [];
			if (node) {
				threshold = JML.domNode2jmlThreshold(node, jml, threshold);
				for (let i = 1; i < length; i++) {
					node = node.nextSibling;
					if (threshold > 0) {
						threshold = JML.domNode2jmlThreshold(node, jml, threshold);
					} else {
						jml.push(JML.computeWeightNode(node));
					}
				}
			}
			return jml;
		}
	}

	getAnnots(xa?: IXAddr, length: number = 1, deep: boolean = true): ISkAnnot[] {
		const search = this._search;
		const filterSearch = search
			? (a: ISkAnnot) => !(a as ISkSearchAnnot).search || (a as ISkSearchAnnot).search === search
			: (a: ISkAnnot) => !(a as ISkSearchAnnot).search;
		const diff = this._diffSession;
		const filter = diff
			? (a: ISkAnnot) => filterSearch(a) && (!(a as ISkDiffAnnot).diffSession || (a as ISkDiffAnnot).diffSession === diff)
			: (a: ISkAnnot) => filterSearch(a) && !(a as ISkDiffAnnot).diffSession;
		if (!xa || (xa.length === 0 && deep)) {
			const an = this.house.schemaDom.getAllAnnots(filter);
			this.house.getStateById<IMoanState>("moan")?.fillAllAnnots(an);
			return an;
		} else {
			const schemaDom = this.house.schemaDom;
			let node = XA.findDomLast(xa, this.house.root);
			const annots: ISkAnnot[] = [];
			for (let i = 0; node && i < length; i++) {
				schemaDom.getAnnots(node, deep, filter, annots);
				node = node.nextSibling;
			}
			this.house.getStateById<IMoanState>("moan")?.fillAnnots(annots, xa, length);
			return annots;
		}
	}

	exportRange(range: IXAddrRange, format: string, formatOptions?: Jsonisable): Jsonisable {
		return this.house.exportRange(range, format, formatOptions);
	}

	exportNodes(xaNodes: IXAddr[], filters: Jsonisable[], format: string, formatOptions?: Jsonisable): Jsonisable {
		return this.house.exportNodes(xaNodes, filters, format, formatOptions);
	}

	extractRange(range: IXAddrRange, batch: IDocBatch, to: IJmlSet): IJmlSet {
		return this.house.schemaDom.exportRange({start: range.start, end: range.end, deletes: batch as any as XmlBatch, forceSplitText: true}, to) as IJmlSet;
	}

	getStruct(xa: IXAddr, virtualPath?: Array<IJmlObj | '#' | '@'>): ISkRuleObj {
		const node = XA.findDom(xa, this.house.root, virtualPath ? xa.length - virtualPath.length : xa.length);
		let struct: SkRuleNode;
		if (node instanceof Element) struct = this.house.schemaDom.getStruct(node);
		else if (node instanceof Attr) return virtualPath ? null : this.house.schemaDom.getStructAtt(node);
		else if (node instanceof Text) return virtualPath ? null : this.house.schemaDom.getStruct(node.parentElement);
		else struct = null;
		if (struct && virtualPath) {
			for (let i = 0; i < virtualPath.length; i++) {
				const n = virtualPath[i];
				if (typeof n === 'object') struct = struct.contentRule.findRuleNodeFor(n);
				else if (n === '@') return struct.contentRule.findRuleAttrFor(xa[xa.length - virtualPath.length + i] as string);
				else return struct.contentRule.findRuleNodeFor(n);//text
				if (!struct) return null;
			}
		}
		return struct;
	}

	getAspect<A>(xa: IXAddr, virtualPath: null | Array<IJmlObj | '#' | '@'>, keyAspect: string, options?: any): A {
		const node = XA.findDom(xa, this.house.root, virtualPath ? xa.length - virtualPath.length : xa.length);
		let eltRule: SkRuleNode;
		if (node instanceof Element) {
			eltRule = this.house.schemaDom.getStruct(node);
		} else if (node instanceof Attr) {
			if (virtualPath) return null;
			const skNode = this.house.schemaDom.getSkNode(node.ownerElement);
			if (!skNode) return null;
			const rule = skNode.rule.contentRule.findRuleAttrFor(node.nodeName);
			return rule ? rule.skMeta.getAspect(keyAspect, rule, skNode, options) : null;
		} else {
			return null;
		}
		const skNode = this.house.schemaDom.getSkNode(node);
		if (!skNode) return null;
		if (eltRule && virtualPath) {
			for (let i = 0; i < virtualPath.length; i++) {
				const n = virtualPath[i];
				if (typeof n === 'object') eltRule = eltRule.contentRule.findRuleNodeFor(n);
				else if (n === '@') {
					const rule = eltRule.contentRule.findRuleAttrFor(xa[xa.length - virtualPath.length + i] as string);
					return rule ? rule.skMeta.getAspect(keyAspect, rule, skNode, options) : null;
				}
				if (!eltRule) return null;
			}
		}
		return eltRule ? eltRule.skMeta.getAspect(keyAspect, eltRule, skNode, options) : null;
	}

	getInsertableStructs(parent: IXAddr, offset1: number | null, offset2?: number | null, attrs?: boolean, replaceables?: number | string): (ISkStructDef | undefined)[][] {
		const node = XA.findDomLast(parent, this.house.root) as Element;
		if (!node) return [];
		const res = offset1 != null || offset2 != null || attrs ? this.house.schemaDom.listInsertableStructs(node, offset1, offset2, attrs) : [];
		if (replaceables != null) {
			if (typeof replaceables === 'number') {
				const repl = node.childNodes.item(replaceables);
				if (repl) res.push(this.house.schemaDom.listAlternateStructs(node, replaceables, repl.nodeType, repl.nodeName));
				else console.log("warning: getInsertableStructs() replaceables out of nodes children.");
			} else {
				res.push(this.house.schemaDom.listAlternateStructs(node, offset1 || 0, ENodeType.attribute, replaceables));
			}
		}
		return res;
	}

	getInsertableStructsAll(what: 'children' | 'atts' | 'both', parent: IXAddr, virtualPath?: Array<IJmlObj>): (ISkStructDef | undefined)[][] {
		const node = XA.findDom(parent, this.house.root, virtualPath ? parent.length - virtualPath.length : parent.length) as Element;
		if (!node) return [];
		if (virtualPath) {
			let parentRule = this.house.schemaDom.getSkNode(node)?.rule;
			for (const anc of virtualPath) {
				parentRule = parentRule.contentRule.findRule((rule) => rule.structMatch(ENodeType.element, JML.jmlNode2name(anc))) as SkRuleNode;
				if (!parentRule) return [];
			}
			const r: ISkStructDef[][] = [];
			if (what !== 'atts') r.push(parentRule?.contentRule?.findRules(r => r instanceof SkRuleNode));
			if (what !== 'children') r.push(parentRule?.contentRule?.findRules(r => r instanceof SkRuleAttr));
			return r;
		} else {
			return this.house.schemaDom.listInsertableStructs(node, what !== 'atts' ? -1 : null, null, what !== 'children');
		}
	}

	getAlternateStructs(parent: IXAddr, offset: number, nodeType: ENodeType, nodeName: string, virtualPath?: Array<IJmlObj>): ISkStructDef[] {
		const node = XA.findDom(parent, this.house.root, virtualPath ? parent.length - virtualPath.length : parent.length) as Element;
		if (!node) return [];
		return this.house.schemaDom.listAlternateStructs(node, offset, nodeType, nodeName, virtualPath);
	}

	getInsertableOffset(parent: IXAddr, nodeType: ENodeType, ifExistAndCardN: 'reject' | 'start' | 'end' = 'reject', nodeName?: string): number {
		const node = XA.findDomLast(parent, this.house.root);
		if (!node) return -2;
		return this.house.schemaDom.getInsertableOffset(node, nodeType, ifExistAndCardN, nodeName);
	}

	getImportPos(insertSeq: IXAddrSeq, content: Node | IJmlObj): EPastePos | EPastePos {
		const node = XA.findDomContainer(insertSeq.start, this.house.root);
		if (!node) return EPastePos.replace; //path virtuel ?
		const offset = XA.last(insertSeq.start);
		if (typeof offset === 'string') return EPastePos.replace;
		return this.house.schemaDom.getPastePos(node, offset, offset + insertSeq.len, content);
	}

	getRealCard(xa: IXAddr): ECard | undefined {
		const node = XA.findDomLast(xa, this.house.root);
		if (node instanceof Element) return this.house.schemaDom.getRealCardElt(node);
		if (node instanceof Attr) return this.house.schemaDom.getRealCardAtt(node);
		return undefined;
	}

	isRemovable(xa: IXAddr): boolean | undefined {
		const node = XA.findDomLast(xa, this.house.root);
		if (node instanceof Element) return this.house.schemaDom.isEltRemovable(node);
		if (node instanceof Attr) return CARD.isOptionnal(this.house.schemaDom.getRealCardAtt(node));
		return undefined;
	}

	checkSchemaOrder(xa: IXAddr, eltName1: string, eltName2: string): boolean | undefined {
		const node = XA.findDomLast(xa, this.house.root);
		if (!node) return undefined;
		return this.house.schemaDom.checkSchemaOrder(node, eltName1, eltName2);
	}


	fetchContent(commands: IDocFetch[]): Promise<IDocFetch[]> {
		return new Promise((resolve, reject) => {
			try {
				for (let i = 0; i < commands.length; i++) {
					const command = commands[i];
					if (command instanceof FetchJml) {
						if (command.threshold > 0) {
							command.result = this.getContentThreshold(command.threshold, command.xa, command.length);
						} else {
							command.result = this.getContent(command.xa, command.length);
						}
					} else if (command instanceof FetchAnnots) {
						command.result = this.getAnnots(command.xa, command.length, command.deep);
					}
				}
				resolve(commands);
			} catch (e) {
				reject(e);
			}
		});
	}

	isMsgFromUs(msg: Msg) {return msg.doorId === this.door.id}

	addMsgListener(listener: (msg: Msg) => void): this {
		this.door.addMsgListener(listener);
		return this;
	}

	removeMsgListener(listener: (msg: Msg) => void): this {
		this.door.removeMsgListener(listener);
		return this;
	}

	getCmtHouse(xAddr: IXAddr): CommentHouseHolder | null {
		const cmt = XA.findDomLast(xAddr, this.house.root);
		return cmt && IS_comment(cmt) ? CommentsState.getCmtHouseFromNode(this.house, cmt) : null;
	}

	newBatch(selBefore?: IXAddrRange): IDocBatch & XmlBatch {
		const batch = new DocXmlBatch(this);
		if (selBefore) batch.setSelBefore(selBefore.start, selBefore.end);
		return batch;
	}

	willUndo(filter?: IMsgFilter): Promise<boolean> {
		return this.house.willUndo(this.door, filter);
	}

	willRedo(filter?: IMsgFilter): Promise<boolean> {
		return this.house.willRedo(this.door, filter);
	}

	setSkSearch(search: ISchemaSearch | null): ISkSearchAnnot[] {
		const newAnnots: ISkSearchAnnot[] = [];
		this.house?.updateSkSearch(search, this._search, newAnnots);
		this._search = search;
		return newAnnots;
	}

	getSkSearch(): ISchemaSearch | null {
		return this._search;
	}

	setDiffSession(diffSession: IXmlDiffSession | null) {
		const annotsToRem: ISkDiffAnnot[] = [];
		const annotsToAdd: ISkDiffAnnot[] = [];
		this.house.doDiff(diffSession, this._diffSession, annotsToAdd, annotsToRem);
		if (annotsToRem.length > 0) {
			this.house.listeners.emit("onSkAnnotsChange", annotsToRem, null);
		}
		this._diffSession = diffSession;
		if (annotsToAdd.length > 0) {
			this.house.listeners.emit("onSkAnnotsChange", null, annotsToAdd);
		}
	}

	/** Retourne la session courante. */
	getDiffSession(): IXmlDiffSession | null {
		return this._diffSession;
	}

	doBatch(batch: XmlBatch) {
		try {
			if (this.noCleanup) batch.setMeta(MSGMETA_noCleanup, true);
			this.house.executeBatch(batch, this.door);
		} catch (e) {
			this.door.dispatchMsg(new ErrorMsg().init(e));
		}
	}

	switchHouse(newHouse: XmlTypedHouse) {
		const newDoor = newHouse.openPublicDoor();
		if (this._houseLstn) this._houseLstn.switchTarget(newHouse.listeners);
		(newDoor as PublicDoor)._listeners = (this.door as PublicDoor)._listeners;
		(this.door as PublicDoor)._listeners = null;
		if (this._search) this.house.updateSkSearch(null, this._search);
		this.house = newHouse;
		this.door = newDoor;
		if (this._search) newHouse.updateSkSearch(this._search, null);
		this.door.dispatchMsg(new ResetStatesMsg().init({} as IXmlResetInitDatas).setHouseId(this.house)); //aucune datas utile.
	}
}

class DocXmlBatch extends XmlBatch implements IDocBatch {

	readonly docHolder: DocHolder;

	get type() {return XmlBatch.type}

	constructor(docHolder: DocHolder) {
		super();
		this.docHolder = docHolder;
	}

	insertJml(xa: IXAddr, jml: IJmlSet): this {
		this.add(new XmlInsertMsg().init(xa, jml));
		return this;
	}

	insertText(xa: IXAddr, text: string): this {
		this.add(new XmlInsertMsg().init(xa, text));
		return this;
	}

	splitText(xa: IXAddr, lenToRemove: number, textToMove: string, jmlToInsert: IJmlSet): this {
		this.add(new XmlSplitMsg().init(xa, lenToRemove, textToMove, [""], null, jmlToInsert));
		return this;
	}

	deleteSequence(xa: IXAddr, length: number): this {
		this.add(new XmlDeleteMsg().init(xa, length));
		return this;
	}

	deleteRange(range: IXAddrRange): this {
		// let m = deleteXmlRange(this.docHolder.house.root, range);
		// if (m) this.add(m);
		if (XA.isAttribute(range.start)) {
			this.setAttr(range.start, null);
		} else {
			this.docHolder.house.schemaDom.exportRange({start: range.start, end: range.end, deletes: this}, null);
		}
		return this;
	}

	spliceSequence(xa: IXAddr, delLen: number, jmlOrText: IJmlSet | string): this {
		if (delLen > 0) this.add(new XmlDeleteMsg().init(xa, delLen));
		this.add(new XmlInsertMsg().init(xa, jmlOrText));
		return this;
	}

	moveSequence(xa: IXAddr, jmlOrText: IJmlSet | string, xaTarget: IXAddr): this {
		this.add(new XmlMoveMsg().init(xa, jmlOrText, xaTarget));
		return this;
	}

	setText(xa: IXAddr, value: string): this {
		this.add(new XmlStrMsg().init(xa, value));
		return this;
	}

	setAttr(xa: IXAddr, value: string | null): this {
		this.add(new XmlStrMsg().init(xa, value));
		return this;
	}

	renameOrMoveAttr(xaAttr: IXAddr, xaTarget: IXAddr): this {
		//if (!xaTarget || !xaAttr) throw Error(`Incorrect start moveAttr(${xaAttr}, ${xaTarget})`);
		//if (!XA.isAttribute(xaAttr)) throw Error(`moveAttr do not point an attribute (${xaAttr})`);
		const attr = XA.findDomLast(xaAttr, this.docHolder.getDocument()) as Attr;
		//if (!attr) throw Error(`Attribute not found (${xaAttr})`);
		this.add(new XmlMoveMsg().init(xaAttr, attr.value, xaTarget));
		return this;
	}

	renameElt(xa: IXAddr, newName: string, fromDoor?: IPublicDoor): this {
		const elt = XA.findDomLast(xa, this.docHolder.getDocument());
		//if (!elt) throw Error(`Element not found (${xa})`);
		//if (elt.nodeType !== Node.ELEMENT_NODE) throw Error(`Node (${xa}) is not an element`);
		const jml = JML.dom2jml(elt);
		(jml[0] as IJmlObj)[''] = newName;
		this.add(new XmlDeleteMsg().init(xa, 1));
		this.add(new XmlInsertMsg().init(xa, jml));
		return this;
	}

	doBatch(): this {
		//console.trace("doBatch", this);
		this.docHolder.doBatch(this);
		return this;
	}

}